import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  NgZone,
  OnDestroy
} from '@angular/core';
import DMSVideo from '@web-components/dms';
import videojs from 'video.js';

@Component({
  selector: 'app-dms-video-player',
  templateUrl: './dms-video-player.component.html',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ['./dms-video-player.component.scss'],
})
export class DmsVideoPlayerComponent implements AfterViewInit, OnDestroy {
  @Input() dmsApi: string;
  @Input() dmsIds: string;
  @Input() autoplay = false;

  showWebComponent = false;
  private player = null;
  private playerPollInterval: any;
  private destroyed = false;
  private resizeObserver?: ResizeObserver;
  private playerSize = {width: 0, height: 0};
  private static dmsVideoInitialised = false;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngAfterViewInit() {
    this.showWebComponent = true;
    this.cdr.detectChanges();

    if (!DmsVideoPlayerComponent.dmsVideoInitialised && typeof DMSVideo === 'function') {
      DMSVideo();
      DmsVideoPlayerComponent.dmsVideoInitialised = true;
    }

    DmsVideoPlayerComponent.disposeOrphanedPlayers();
    this.resolvePlayer();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    if (this.playerPollInterval) {
      clearInterval(this.playerPollInterval);
    }
    this.disposePlayers();
  }

  togglePlay() {
    if (!this.player) {
      return;
    }
    if (this.player.paused()) {
      this.player.play();
    } else {
      this.player.pause();
    }
  }

  private disposePlayers() {
    const host: HTMLElement = this.elementRef.nativeElement;
    host.querySelectorAll('.video-js').forEach((element: Element) => {
      const player: any = videojs.getPlayer(element as HTMLElement);
      if (player && !player.isDisposed()) {
        player.dispose();
      }
    });
    this.player = null;
    host.querySelectorAll('kit-dms-video').forEach((element: Element) => element.remove());
  }

  private static disposeOrphanedPlayers() {
    const players: Record<string, any> = videojs.getPlayers();
    Object.keys(players).forEach(id => {
      const player = players[id];
      if (player && !player.isDisposed() && !player.el()?.isConnected) {
        player.dispose();
      }
    });
  }

  private resolvePlayer() {
    let attempts = 0;
    const maxAttempts = 20;

    this.playerPollInterval = setInterval(() => {
      if (this.destroyed) {
        clearInterval(this.playerPollInterval);
        return;
      }

      const container = this.elementRef.nativeElement.querySelector(`.video-js`);
      this.player = container ? videojs.getPlayer(container) : null;

      if (this.player || ++attempts >= maxAttempts) {
        clearInterval(this.playerPollInterval);
        if (this.player) {
          this.fitPlayerToHost();
          this.observeHostResize();
          if (this.autoplay) {
            this.player.play();
          }
        }
      }
    }, 100);
  }

  private observeHostResize() {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.fitPlayerToHost());
      this.resizeObserver.observe(this.elementRef.nativeElement);
    });
  }

  /**
   * `kit-dms-video` leaves video.js in fluid mode, where the player height follows
   * its width and overflows the carousel when the media drawer is resized. Size the
   * player from the host box instead, minus whatever chrome the web component adds.
   */
  private fitPlayerToHost() {
    const host: HTMLElement = this.elementRef.nativeElement;
    const playerElement: HTMLElement = host.querySelector('.video-js');
    if (!this.player || !playerElement || !host.clientHeight) {
      return;
    }
    const chromeHeight =
      playerElement.getBoundingClientRect().top - host.getBoundingClientRect().top;
    const width = host.clientWidth;
    const height = Math.max(0, host.clientHeight - chromeHeight);
    if (width === this.playerSize.width && height === this.playerSize.height) {
      return;
    }
    this.playerSize = {width, height};
    this.player.fluid(false);
    this.player.dimensions(width, height);
  }
}
